import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  AppUser,
  Buyer,
  DocumentsSummary,
  Supplier,
  SupplierInvoices,
  SupplierSalesReceipts,
} from '@dill/dill-shared';
import {
  addDistributionOneSupplierConnectionExtraReducer,
  addSage100SupplierConnectionExtraReducer,
  assignDriverToInvoiceExtraReducer,
  getSupplierSummaryExtraReducer,
  getUserCustomerExtraReducer,
  getUserCustomersExtraReducer,
  getUserDriversExtraReducer,
  getUserInvoicesExtraReducer,
  getUserSalesReceiptsExtraReducer,
  getUserSuppliersExtraReducer,
  sendEmailStatementsForBuyersExtraReducer,
  sendEmailStatementsForAllBuyersExtraReducer,
  addP21SupplierConnectionExtraReducer,
  addEclipseSupplierConnectionExtraReducer,
  recursiveGetUserCustomersExtraReducer,
  updateSupplierReminderSettingsExtraReducer,
  getMainBuyerForPreviewExtraReducer,
  removeSupplierUserExtraReducer,
  updateUserExtraReducer,
} from '../reducers/suppliersReducers';

export interface SuppliersInitialState {
  isLoading: boolean;
  loadingList: string[];
  error: string | null;
  suppliers: Supplier[];
  selectedSupplier: Supplier | null;
  supplierTotalDocumentsSummary: DocumentsSummary | null;
  customers: {
    buyer: Buyer;
    usersEmailsDetails: {
      email: string;
      isStatementsEmailOptOut: boolean;
      isPaymentRemindersEmailOptOut: boolean;
      isInvitedUser: boolean;
    }[];
    documentsSummary: DocumentsSummary;
  }[];
  // Supplier the `customers` list currently belongs to. Used to drop late/stale
  // customer fetch responses from a previously selected supplier (see suppliersReducers).
  customersSupplierId: string | null;
  supplierDrivers: AppUser[];
  supplierUsers: AppUser[];
  userSupplierInvoices: SupplierInvoices | null;
  userSupplierInvoicesDataWeeksLimit: number;
  userSupplierSalesReceipts: SupplierSalesReceipts | null;
  userSupplierSalesReceiptsDataWeeksLimit: number;
}

const initialState: SuppliersInitialState = {
  isLoading: false,
  loadingList: [],
  error: null,
  supplierUsers: [],
  supplierTotalDocumentsSummary: null,
  suppliers: [],
  customers: [],
  customersSupplierId: null,
  supplierDrivers: [],
  selectedSupplier: null,
  userSupplierInvoices: null,
  userSupplierInvoicesDataWeeksLimit: 2,
  userSupplierSalesReceipts: null,
  userSupplierSalesReceiptsDataWeeksLimit: 2,
};

export const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    resetSuppliersRootState: () => initialState,
    selectSupplier: (state, action: PayloadAction<Supplier | null>) => {
      state.selectedSupplier = action.payload;
    },
    updateUserSupplierInvoicesDataWeeksLimit: (state, action: PayloadAction<number>) => {
      state.userSupplierInvoicesDataWeeksLimit = action.payload;
    },
    updateUserSupplierSalesReceiptsDataWeeksLimit: (state, action: PayloadAction<number>) => {
      state.userSupplierSalesReceiptsDataWeeksLimit = action.payload;
    },
    // Patches isFlagged/flaggedByUserId on the already-loaded `customers` list in place after a
    // successful flag/unflag update, instead of triggering a full customers re-fetch just to
    // reflect a one-field change.
    updateCustomerBuyerFlags: (
      state,
      action: PayloadAction<{
        buyerIds: string[];
        isFlagged: boolean;
        flaggedByUserId?: string | null;
      }>
    ) => {
      const { buyerIds, isFlagged, flaggedByUserId } = action.payload;
      const idSet = new Set(buyerIds);
      state.customers = state.customers.map((c) =>
        idSet.has(c.buyer.id)
          ? {
              ...c,
              buyer: {
                ...c.buyer,
                isFlagged,
                ...(flaggedByUserId !== undefined ? { flaggedByUserId } : {}),
              },
            }
          : c
      );
    },
    // Same reasoning as updateCustomerBuyerFlags above - patches the assigned credit manager
    // and/or sales rep on one already-loaded customer row in place, so the Customers table
    // doesn't need a full re-fetch (and the visible reload/scroll-reset that comes with it) just
    // to reflect an inline "+ Assign" pick.
    updateCustomerBuyerAssignment: (
      state,
      action: PayloadAction<{
        buyerId: string;
        asignedCreditManagerId?: string | null;
        salesRepId?: string | null;
      }>
    ) => {
      const { buyerId, ...updates } = action.payload;
      state.customers = state.customers.map((c) =>
        c.buyer.id === buyerId ? { ...c, buyer: { ...c.buyer, ...updates } } : c
      );
    },
  },
  extraReducers(builder) {
    getUserSuppliersExtraReducer(builder);
    getUserCustomersExtraReducer(builder);
    getSupplierSummaryExtraReducer(builder);
    getUserInvoicesExtraReducer(builder);
    getUserDriversExtraReducer(builder);
    assignDriverToInvoiceExtraReducer(builder);
    getUserSalesReceiptsExtraReducer(builder);
    sendEmailStatementsForBuyersExtraReducer(builder);
    addDistributionOneSupplierConnectionExtraReducer(builder);
    addSage100SupplierConnectionExtraReducer(builder);
    addP21SupplierConnectionExtraReducer(builder);
    addEclipseSupplierConnectionExtraReducer(builder);
    sendEmailStatementsForAllBuyersExtraReducer(builder);
    getUserCustomerExtraReducer(builder);
    recursiveGetUserCustomersExtraReducer(builder);
    updateSupplierReminderSettingsExtraReducer(builder);
    getMainBuyerForPreviewExtraReducer(builder);
    removeSupplierUserExtraReducer(builder);
    updateUserExtraReducer(builder);
  },
});

export const {
  resetSuppliersRootState,
  selectSupplier,
  updateUserSupplierInvoicesDataWeeksLimit,
  updateUserSupplierSalesReceiptsDataWeeksLimit,
  updateCustomerBuyerFlags,
  updateCustomerBuyerAssignment,
} = suppliersSlice.actions;
